// cmds/ExternalDataCmds.js
/**
 * @fileoverview
 *
 * UserCmds for the third-party database lookups in `src/integrations/` and `WebServices.js`.
 *
 * ─── Why these exist ────────────────────────────────────────────────────────
 *
 * The five External Data Retrieval ActionDefs declared `serverCommand: '<name>'`, where `<name>`
 * was a plain exported JavaScript function that `fetch`es a REST API from the browser. Nothing
 * reaches bfd-server, so `dispatchServerCommand` had no entry for any of them and each fell
 * through to `throw new Error('Unknown serverCommand: ...')`. They carried
 * `blockedOn: 'server-route'` for that reason.
 *
 * The route they were waiting for already exists and is the one every other reachable action
 * uses: a `UserCmd` named by the ActionDef's `userAction`. `dispatchServerCommand`'s own
 * `cmdMap` arm cannot succeed for ANYTHING -- `ServerCmd` has no `execute` method, and
 * `cmdData(...args)` spreads positionally into a command-text builder rather than taking an
 * options object -- so building a second, parallel route for these would have meant inventing a
 * mechanism beside a working one.
 *
 * Going through `UserCmd` also means they are covered by the checks that already guard the
 * other actions: `test/gibbs/actionCalleeContract.test.js` can reach a callee only through
 * `UserActions[name].cmd.doFn`, so a declaration that stops fitting its lookup now fails a test
 * instead of a chemist's workflow.
 *
 * ─── noEffect ───────────────────────────────────────────────────────────────
 *
 * Every one of these is a read: build a URL, GET or POST it, return what came back. None
 * mutates the workspace, so all are `noEffect: true` and none enters the undo stack. (Contrast
 * `updateCompoundWithChemblId`, which caches onto the compound -- see the note on ChEMBL below.)
 *
 * ─── ChEMBL is deliberately absent ──────────────────────────────────────────
 *
 * `lookup-chembl` is NOT wrapped here and stays blocked. It is not the same problem: there is no
 * function named `chemblLookup`, and the single ActionDef conflates three that take different
 * arguments and return different things --
 * `chemblLookupCompoundByInchikey`/`-BySmiles` (one string -> `{molecules}`),
 * `chemblLookupByUniprotId` (one string -> `{targets}`), and
 * `chemblLookupActivity` (TWO ids -> `{activities}`). Landing it means splitting the definition,
 * not adding a command, and the compound half wants
 * `updateCompoundWithChemblId(compound, trigger, {allowTautomers})` -- which is where
 * `allowTautomers` is actually read, but which also writes a cached property (so NOT
 * `noEffect`) and needs a `trigger` for `ScopedPropertyGroup.updateRequestStatus`. It has no
 * caller anywhere outside `integrations/chembl.js` today, so binding it would be its first.
 *
 * @typedef ExternalDataCmds
 * @type {object}
 * @property {getCCDSmiles} LookupCcdSmiles
 * @property {uniprotLookup} LookupUniprot
 * @property {klifsLookupByPdbIds} LookupKlifs
 * @property {pubChemLookup} LookupPubchem
 */
import { UserCmd } from 'BMapsCmds/UserCmd';
import { getCCDSmiles } from 'BMapsSrc/integrations/rcsb';
import { uniprotLookup } from 'BMapsSrc/integrations/uniprot';
import { klifsLookupByPdbIds } from 'BMapsSrc/integrations/klifs';
import { pubChemLookup } from 'BMapsSrc/WebServices';

/** @type {ExternalDataCmds} */
export const ExternalDataCmds = {
    // `getCCDSmiles(ccdCode)` -> `{ smiles }` | `{ errors: string[] }`. A chemical COMPONENT
    // (ligand/CCD) code such as "ATP", not a 4-character PDB entry id.
    LookupCcdSmiles: new UserCmd('LookupCcdSmiles', getCCDSmiles, { noEffect: true }),

    // `uniprotLookup(pdbIds)` -> `{ results: {[pdbId]: string[]} }` | `{ error }`. Takes a
    // string or an array (it calls `ensureArray`), and LOWERCASES the keys of what it returns.
    LookupUniprot: new UserCmd('LookupUniprot', uniprotLookup, { noEffect: true }),

    // `klifsLookupByPdbIds(pdbIds)` -> `{ results: {[pdbId]: object[]} }` | `{ error }`. Also
    // ensureArray + lowercased keys, and it validates the ids itself, returning a readable
    // `{ error }` for an invalid or empty list rather than throwing.
    LookupKlifs: new UserCmd('LookupKlifs', klifsLookupByPdbIds, { noEffect: true }),

    // `pubChemLookup({ sdf?, smiles?, name? })` -> the raw PubChem PropertyTable row
    // `{ CID, Title, IUPACName }`, or `{}` when nothing matched. ONE named object, which is why
    // `lookup-pubchem` declares `argStyle: 'options'` and three flat optional string inputs
    // instead of a single input of type 'object' -- 'object' is not in PLAN_LITERAL_TYPES, so
    // the old declaration could never have been satisfied from a plan even with a route.
    // The field names here are load-bearing and the order is not; the ActionDef's declared
    // input names are exactly the fields this reads.
    LookupPubchem: new UserCmd('LookupPubchem', pubChemLookup, { noEffect: true }),
};
